// src/App.js
import React, { useState, useEffect, useRef } from 'react';
import ReactGA from 'react-ga4'; // Import GA4
import api from './api'; // Import the Axios instance
import {
  FaFolder,
  FaFolderOpen,
  FaFileAlt,
  FaFilePdf, // Imported PDF icon
  FaMusic,
  FaVideo,
  FaTimes,
  FaArrowLeft,
  FaArrowRight,
} from 'react-icons/fa';
import Modal from 'react-modal';
import logo from './logo.png';
import './App.css'; // Import the CSS file for styles

Modal.setAppElement('#root'); // Set your root element for accessibility

function App() {
  const [files, setFiles] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [modalFileName, setModalFileName] = useState('');
  const [modalFileDetails, setModalFileDetails] = useState(null);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [currentFileList, setCurrentFileList] = useState([]);
  const modalRef = useRef(null);

  useEffect(() => {
    // Initialize GA4 with your Measurement ID
    ReactGA.initialize('G-LPXTDLVLG1'); // Replace with your actual Measurement ID

    // Track the initial page view
    ReactGA.send({ hitType: 'pageview', page: window.location.pathname + window.location.search });

    fetchFiles('./dir'); // Load root directory on page load
  }, []);

  const fetchFiles = (path) => {
    api
      .get(`/list-directory?path=${encodeURIComponent(path)}`)
      .then((response) => {
        // Sort the files and folders by name
        const sortedFiles = response.data.sort((a, b) => {
          const nameA = a.name.toLowerCase();
          const nameB = b.name.toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setFiles((files) => ({ ...files, [path]: sortedFiles }));
      })
      .catch((err) => console.error(err));
  };

  const handleFolderToggle = (folderPath) => {
    setExpandedFolders({
      ...expandedFolders,
      [folderPath]: !expandedFolders[folderPath],
    });

    if (!files[folderPath]) {
      fetchFiles(folderPath);
    }

    // Send a custom event to GA4
    ReactGA.event({
      category: 'Folder Interaction',
      action: expandedFolders[folderPath] ? 'Collapse Folder' : 'Expand Folder',
      label: folderPath,
    });
  };

  const renderIcon = (file) => {
    if (file.isDirectory) return <FaFolder />;
    const ext = file.extension.toLowerCase();
    if (ext === '.mp3') return <FaMusic />;
    if (['.mp4', '.mov', '.avi', '.mkv'].includes(ext)) return <FaVideo />;
    if (ext === '.pdf') return <FaFilePdf />; // Render PDF icon
    return <FaFileAlt />;
  };

  const openModal = async (
    filePath,
    relativePath,
    fileName,
    fileDetails,
    fileIndex,
    fileList
  ) => {
    const ext = filePath.substring(filePath.lastIndexOf('.')).toLowerCase();
    setModalFileName(fileName);
    setModalFileDetails(fileDetails);
    setCurrentFileIndex(fileIndex);
    setCurrentFileList(fileList);
    const baseUrl = process.env.REACT_APP_API_BASE_URL || '';

    if (['.png', '.jpg', '.jpeg', '.gif'].includes(ext)) {
      // Images
      const imageUrl = `${baseUrl}/images/${relativePath}`;
      setPreviewContent(imageUrl);
    } else if (ext === '.pdf') {
      // PDFs
      const pdfUrl = `${baseUrl}/pdfs/${relativePath}`;
      setPreviewContent(pdfUrl);
    } else if (['.mp4', '.mov', '.avi', '.mkv'].includes(ext)) {
      // Videos
      const videoUrl = `${baseUrl}/videos/${relativePath}`;
      setPreviewContent(videoUrl);
    } else if (['.mp3', '.wav', '.aac'].includes(ext)) {
      // Audios
      const audioUrl = `${baseUrl}/audio/${relativePath}`;
      setPreviewContent(audioUrl);
    } else {
      // For other text files
      try {
        const response = await api.get(
          `/preview-file?path=${encodeURIComponent(filePath)}`
        );
        setPreviewContent(response.data);
      } catch (error) {
        console.error('Error fetching file preview:', error);
        setPreviewContent('Unable to preview this file.');
      }
    }
    setModalIsOpen(true);

    // Send a custom event to GA4
    ReactGA.event({
      category: 'File Interaction',
      action: 'Open File',
      label: filePath,
    });
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setPreviewContent('');
    setModalFileName('');
    setModalFileDetails(null);
    setCurrentFileIndex(0);
    setCurrentFileList([]);
  };

  const showPrevFile = () => {
    if (currentFileIndex > 0) {
      const prevIndex = currentFileIndex - 1;
      const prevFile = currentFileList[prevIndex];
      openModal(
        prevFile.fullPath,
        prevFile.relativePath,
        prevFile.name,
        prevFile,
        prevIndex,
        currentFileList
      );
    }
  };

  const showNextFile = () => {
    if (currentFileIndex < currentFileList.length - 1) {
      const nextIndex = currentFileIndex + 1;
      const nextFile = currentFileList[nextIndex];
      openModal(
        nextFile.fullPath,
        nextFile.relativePath,
        nextFile.name,
        nextFile,
        nextIndex,
        currentFileList
      );
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (modalIsOpen) {
        if (event.key === 'ArrowLeft') {
          showPrevFile();
        } else if (event.key === 'ArrowRight') {
          showNextFile();
        } else if (event.key === 'Escape') {
          closeModal();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalIsOpen, currentFileIndex, currentFileList]);

  // Add touch event handlers for swipe gestures on mobile
  useEffect(() => {
    const modalElement = modalRef.current;
    if (!modalElement) return;

    let touchStartX = 0;
    let touchEndX = 0;

    const handleTouchStart = (e) => {
      touchStartX = e.changedTouches[0].screenX;
    };

    const handleTouchEnd = (e) => {
      touchEndX = e.changedTouches[0].screenX;
      handleGesture();
    };

    const handleGesture = () => {
      if (touchEndX < touchStartX - 50) {
        // Swiped left
        showNextFile();
      }

      if (touchEndX > touchStartX + 50) {
        // Swiped right
        showPrevFile();
      }
    };

    modalElement.addEventListener('touchstart', handleTouchStart);
    modalElement.addEventListener('touchend', handleTouchEnd);

    return () => {
      modalElement.removeEventListener('touchstart', handleTouchStart);
      modalElement.removeEventListener('touchend', handleTouchEnd);
    };
  }, [modalIsOpen, currentFileIndex, currentFileList]);

  const renderFileInfo = (file) => (
    <>
      <td
        className="date-added"
        data-label="Date Added"
        title={new Date(file.modifiedTime).toLocaleString()}
      >
        {new Date(file.modifiedTime).toLocaleString()}
      </td>
      <td className="size" data-label="Size">
        {file.isDirectory ? '' : `${file.size} KB`}
      </td>
      <td className="kind" data-label="Kind">
        {file.isDirectory ? 'Folder' : file.extension.toUpperCase()}
      </td>
    </>
  );

  const renderFolderContent = (folderPath, depth = 0) => {
    if (!files[folderPath]) return null;
    return files[folderPath].map((file, index) => (
      <React.Fragment key={file.fullPath}>
        <tr
          className={`${
            file.isDirectory ? 'folder-row' : 'file-row'
          } ${expandedFolders[file.fullPath] ? 'expanded' : ''}`}
        >
          <td
            className={`name-cell ${
              file.isDirectory ? 'folder-name-cell' : ''
            } ${depth > 0 ? 'nested' : ''}`}
            onClick={() => file.isDirectory && handleFolderToggle(file.fullPath)}
            style={{
              cursor: file.isDirectory ? 'pointer' : 'default',
              position: 'relative',
              paddingLeft:
                window.innerWidth > 768
                  ? depth > 0
                    ? `${depth * 20 + 10}px`
                    : '10px' // Apply desktop padding
                  : depth > 0
                  ? `${depth * 20 + 20}px`
                  : '10px', // Apply mobile padding
            }}
            data-label="Name"
          >
            <span className="icon">
              {expandedFolders[file.fullPath] && file.isDirectory ? (
                <FaFolderOpen />
              ) : (
                renderIcon(file)
              )}
            </span>
            {file.isDirectory ? (
              <span className="folder-name">{file.name}</span>
            ) : (
              <>
                <a
                  onClick={() => {
                    const previewableExtensions = [
                      '.png',
                      '.jpg',
                      '.jpeg',
                      '.gif',
                      '.pdf',
                      '.mp4',
                      '.mov',
                      '.avi',
                      '.mkv',
                      '.mp3',
                      '.wav',
                      '.aac',
                    ];
                    const currentFolderFiles = files[folderPath].filter(
                      (f) =>
                        !f.isDirectory &&
                        previewableExtensions.includes(f.extension.toLowerCase())
                    );
                    const fileIndex = currentFolderFiles.findIndex(
                      (f) => f.fullPath === file.fullPath
                    );
                    openModal(
                      file.fullPath,
                      file.relativePath,
                      file.name,
                      file,
                      fileIndex,
                      currentFolderFiles
                    );
                  }}
                  style={{
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    color: '#FFFFFF',
                  }}
                >
                  {file.name}
                </a>
                <div className="file-info-mobile">{`${new Date(
                  file.modifiedTime
                ).toLocaleString()} | ${file.size} KB`}</div>
              </>
            )}
          </td>
          {renderFileInfo(file)}
        </tr>
        {expandedFolders[file.fullPath] &&
          renderFolderContent(file.fullPath, depth + 1)}
      </React.Fragment>
    ));
  };

  return (
    <div className="App">
      <header className="header">
        <div className="logo">
          <img src={logo} alt="Logo" />
        </div>
        <h1 className="title">
          <a
            href="https://www.instagram.com/ganan.s/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Ganan's SSD
          </a>
        </h1>
      </header>
      <div className="content">
        <table className="file-table">
          <colgroup>
            <col style={{ width: '40%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '20%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>Name</th>
              <th>Date Added</th>
              <th>Size</th>
              <th>Kind</th>
            </tr>
          </thead>
          <tbody>{renderFolderContent('./dir')}</tbody>
        </table>

        <Modal
          isOpen={modalIsOpen}
          onRequestClose={closeModal}
          style={{
            overlay: {
              backgroundColor: 'rgba(11, 12, 13, 0.95)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            },
            content: {
              position: 'relative',
              maxWidth: '90%',
              maxHeight: '90%',
              overflow: 'auto',
              backgroundColor: '#0B0C0D',
              color: '#FFFFFF',
              padding: '20px',
              border: 'none',
              borderRadius: '8px',
              inset: 'auto',
            },
          }}
          contentLabel="File Preview Modal"
        >
          <div ref={modalRef}>
            <button
              onClick={closeModal}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'none',
                border: 'none',
                color: '#FFFFFF',
                fontSize: '24px',
                cursor: 'pointer',
              }}
            >
              <FaTimes />
            </button>

            <h2 style={{ marginTop: '0', textAlign: 'center' }}>
              {modalFileName}
            </h2>

            {modalFileDetails && (
              <>
                {['.png', '.jpg', '.jpeg', '.gif'].includes(
                  modalFileDetails.extension.toLowerCase()
                ) && (
                  <div style={{ position: 'relative' }}>
                    <img
                      src={previewContent}
                      alt="Preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '60vh',
                        height: 'auto',
                        display: 'block',
                        margin: '10px auto',
                      }}
                    />
                    {currentFileIndex > 0 && (
                      <button
                        onClick={showPrevFile}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '10px',
                          transform: 'translateY(-50%)',
                          background: 'rgba(0, 0, 0, 0.5)',
                          border: 'none',
                          color: '#FFFFFF',
                          fontSize: '24px',
                          cursor: 'pointer',
                          padding: '10px',
                        }}
                      >
                        <FaArrowLeft />
                      </button>
                    )}
                    {currentFileIndex < currentFileList.length - 1 && (
                      <button
                        onClick={showNextFile}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          right: '10px',
                          transform: 'translateY(-50%)',
                          background: 'rgba(0, 0, 0, 0.5)',
                          border: 'none',
                          color: '#FFFFFF',
                          fontSize: '24px',
                          cursor: 'pointer',
                          padding: '10px',
                        }}
                      >
                        <FaArrowRight />
                      </button>
                    )}
                  </div>
                )}

                {modalFileDetails.extension.toLowerCase() === '.pdf' && (
                  <div style={{ position: 'relative' }}>
                    <iframe
                      src={previewContent}
                      title="PDF Preview"
                      style={{
                        width: '100%',
                        height: '60vh',
                        border: 'none',
                        display: 'block',
                        margin: '10px auto',
                      }}
                    ></iframe>
                    {currentFileIndex > 0 && (
                      <button
                        onClick={showPrevFile}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '10px',
                          transform: 'translateY(-50%)',
                          background: 'rgba(0, 0, 0, 0.5)',
                          border: 'none',
                          color: '#FFFFFF',
                          fontSize: '24px',
                          cursor: 'pointer',
                          padding: '10px',
                        }}
                      >
                        <FaArrowLeft />
                      </button>
                    )}
                    {currentFileIndex < currentFileList.length - 1 && (
                      <button
                        onClick={showNextFile}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          right: '10px',
                          transform: 'translateY(-50%)',
                          background: 'rgba(0, 0, 0, 0.5)',
                          border: 'none',
                          color: '#FFFFFF',
                          fontSize: '24px',
                          cursor: 'pointer',
                          padding: '10px',
                        }}
                      >
                        <FaArrowRight />
                      </button>
                    )}
                  </div>
                )}

                {['.mp4', '.mov', '.avi', '.mkv'].includes(
                  modalFileDetails.extension.toLowerCase()
                ) && (
                  <div style={{ position: 'relative' }}>
                    <video
                      src={previewContent}
                      controls
                      style={{
                        maxWidth: '100%',
                        maxHeight: '60vh',
                        display: 'block',
                        margin: '10px auto',
                      }}
                    ></video>
                    {currentFileIndex > 0 && (
                      <button
                        onClick={showPrevFile}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '10px',
                          transform: 'translateY(-50%)',
                          background: 'rgba(0, 0, 0, 0.5)',
                          border: 'none',
                          color: '#FFFFFF',
                          fontSize: '24px',
                          cursor: 'pointer',
                          padding: '10px',
                        }}
                      >
                        <FaArrowLeft />
                      </button>
                    )}
                    {currentFileIndex < currentFileList.length - 1 && (
                      <button
                        onClick={showNextFile}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          right: '10px',
                          transform: 'translateY(-50%)',
                          background: 'rgba(0, 0, 0, 0.5)',
                          border: 'none',
                          color: '#FFFFFF',
                          fontSize: '24px',
                          cursor: 'pointer',
                          padding: '10px',
                        }}
                      >
                        <FaArrowRight />
                      </button>
                    )}
                  </div>
                )}

                {['.mp3', '.wav', '.aac'].includes(
                  modalFileDetails.extension.toLowerCase()
                ) && (
                  <div style={{ position: 'relative', textAlign: 'center' }}>
                    <audio
                      src={previewContent}
                      controls
                      style={{
                        width: '100%',
                        display: 'block',
                        margin: '10px auto',
                      }}
                    ></audio>
                    {currentFileIndex > 0 && (
                      <button
                        onClick={showPrevFile}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '10px',
                          transform: 'translateY(-50%)',
                          background: 'rgba(0, 0, 0, 0.5)',
                          border: 'none',
                          color: '#FFFFFF',
                          fontSize: '24px',
                          cursor: 'pointer',
                          padding: '10px',
                        }}
                      >
                        <FaArrowLeft />
                      </button>
                    )}
                    {currentFileIndex < currentFileList.length - 1 && (
                      <button
                        onClick={showNextFile}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          right: '10px',
                          transform: 'translateY(-50%)',
                          background: 'rgba(0, 0, 0, 0.5)',
                          border: 'none',
                          color: '#FFFFFF',
                          fontSize: '24px',
                          cursor: 'pointer',
                          padding: '10px',
                        }}
                      >
                        <FaArrowRight />
                      </button>
                    )}
                  </div>
                )}

                {!['.png', '.jpg', '.jpeg', '.gif', '.pdf', '.mp4', '.mov', '.avi', '.mkv', '.mp3', '.wav', '.aac'].includes(
                  modalFileDetails.extension.toLowerCase()
                ) && (
                  <div style={{ whiteSpace: 'pre-wrap', margin: '10px 0' }}>
                    {previewContent}
                  </div>
                )}
              </>
            )}

            {modalFileDetails && (
              <div className="file-details">
                <p>
                  <strong>Date Modified:</strong>{' '}
                  {new Date(modalFileDetails.modifiedTime).toLocaleString()}
                </p>
                <p>
                  <strong>Size:</strong>{' '}
                  {modalFileDetails.isDirectory
                    ? 'Folder'
                    : `${modalFileDetails.size} KB`}
                </p>
                <p>
                  <strong>Type:</strong>{' '}
                  {modalFileDetails.isDirectory
                    ? 'Folder'
                    : modalFileDetails.extension.toUpperCase()}
                </p>
              </div>
            )}
          </div>
        </Modal>
      </div>

      <footer className="footer">
        <p>© Ganan Sivagnanenthirarajah</p>
        <p>
          Last modified{' '}
          {new Date(document.lastModified).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </p>
      </footer>
    </div>
  );
}

export default App;